import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Calendar, FileText, Shield } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { slugifyHeading } from '@/lib/legal/slugifyHeading';
import { cn } from '@/lib/utils';

export interface LegalDocumentSection {
  id: string;
  title: string;
}

interface LegalDocumentPageProps {
  title: string;
  subtitle: string;
  updatedAt: string;
  sections: LegalDocumentSection[];
  markdownContent: string;
  /** Link to the companion legal document (terms <-> privacy). */
  companion: { to: string; label: string };
}

const getTextContent = (children: unknown): string => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  const props = (children as { props?: { children?: unknown } } | null)?.props;
  if (props?.children) return getTextContent(props.children);
  return '';
};

const LegalDocumentPage = ({
  title,
  subtitle,
  updatedAt,
  sections,
  markdownContent,
  companion,
}: LegalDocumentPageProps) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    setActiveSection('');
  }, [markdownContent]);

  useEffect(() => {
    const handleScroll = () => {
      const headings = document.querySelectorAll('h2[id]');
      let current = '';

      headings.forEach((heading) => {
        const headingTop = heading.getBoundingClientRect().top;
        if (headingTop <= 100) {
          current = heading.id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      className="onboarding-surface min-h-dvh"
      data-testid="legal-document-page"
    >
      <a
        href="#legal-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        {t('legalPage.skipToContent')}
      </a>

      <header className="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center">
            <img
              src="/images/autoclipper-logo-full.png"
              alt={t('legalPage.logoAlt')}
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <Link
              to={companion.to}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              {companion.label}
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 lg:gap-8">
          <aside className="lg:col-span-1">
            <Card className="lg:sticky lg:top-24">
              <CardContent className="p-5 sm:p-6">
                <div className="mb-4 flex items-center gap-2.5">
                  <FileText className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">
                    {t('legalPage.toc')}
                  </h2>
                </div>
                <Separator className="mb-4" />
                <nav className="flex flex-wrap gap-1.5 lg:max-h-[min(28rem,calc(100dvh-14rem))] lg:flex-col lg:flex-nowrap lg:overflow-y-auto">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'rounded-md px-3 py-2 text-left text-sm transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        activeSection === section.id
                          ? 'bg-primary/10 font-medium text-primary'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                      )}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          <main id="legal-content" className="lg:col-span-3">
            <Card>
              <CardContent className="p-6 sm:p-8 lg:p-10">
                <div className="mb-8">
                  <div className="mb-5 flex items-start gap-4">
                    <div className="rounded-lg border border-primary/20 bg-primary/10 p-2.5">
                      <Shield className="h-6 w-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                        {title}
                      </h1>
                      <p className="mt-2 text-base text-muted-foreground">
                        {subtitle}
                      </p>
                      <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>{t('legalPage.updatedAt', { date: updatedAt })}</span>
                      </p>
                    </div>
                  </div>
                  <Separator />
                </div>

                <article
                  className="prose prose-invert max-w-none
                    prose-headings:scroll-mt-24 prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-foreground
                    prose-h1:mb-4 prose-h1:mt-0 prose-h1:text-3xl
                    prose-h2:mb-4 prose-h2:mt-10 prose-h2:border-b prose-h2:border-border prose-h2:pb-3 prose-h2:text-xl
                    prose-h3:mb-3 prose-h3:mt-6 prose-h3:text-lg
                    prose-p:mb-4 prose-p:text-base prose-p:leading-relaxed prose-p:text-muted-foreground
                    prose-li:my-1.5 prose-li:text-base prose-li:leading-relaxed prose-li:text-muted-foreground
                    prose-strong:font-semibold prose-strong:text-foreground
                    prose-a:font-medium prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                    prose-ul:my-4 prose-ol:my-4
                    prose-code:rounded prose-code:bg-secondary prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:text-primary
                    [&>h1:first-child]:hidden"
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children, ...props }) => (
                        <h1 id={slugifyHeading(getTextContent(children))} {...props}>
                          {children}
                        </h1>
                      ),
                      h2: ({ children, ...props }) => (
                        <h2 id={slugifyHeading(getTextContent(children))} {...props}>
                          {children}
                        </h2>
                      ),
                      h3: ({ children, ...props }) => (
                        <h3 id={slugifyHeading(getTextContent(children))} {...props}>
                          {children}
                        </h3>
                      ),
                      ul: ({ ...props }) => (
                        <ul className="list-disc pl-6" {...props} />
                      ),
                      ol: ({ ...props }) => (
                        <ol className="list-decimal pl-6" {...props} />
                      ),
                      a: ({ href, ...props }) => (
                        <a
                          href={href}
                          className="underline-offset-2"
                          target={href?.startsWith('http') ? '_blank' : undefined}
                          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                          {...props}
                        />
                      ),
                      code: ({ className, ...props }) => {
                        const isInline = !className?.includes('language-');
                        return isInline ? (
                          <code {...props} />
                        ) : (
                          <code className={cn('block', className)} {...props} />
                        );
                      },
                    }}
                  >
                    {markdownContent}
                  </ReactMarkdown>
                </article>

                <div className="mt-12 border-t border-border pt-6">
                  <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/50 p-4 sm:p-5">
                    <Shield className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="mb-1.5 text-sm font-semibold text-foreground">
                        {t('legalPage.questionsTitle')}
                      </p>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        <Trans
                          i18nKey="legalPage.questionsBody"
                          components={{
                            email: (
                              <a
                                href="mailto:contato@autoclipper.live"
                                className="font-medium text-primary hover:underline"
                              />
                            ),
                          }}
                        />
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {t('legalPage.seeAlso')}:{' '}
                        <Link
                          to={companion.to}
                          className="font-medium text-primary hover:underline"
                        >
                          {companion.label}
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentPage;
